import {
  Typography,
  Chip,
  Box,
  TextField,
  ButtonBase,
  FormHelperText,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { EmployeeDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.EmployeeAutocomplete;

type Props = {
  employees: Array<EmployeeDto>;
  name: string;
  label: string;
  disabled?: boolean;
  onChange: (value: Array<string>) => void;
  value: Array<string>;
  errorMessage?: string;
  helperText?: string;
  emptyValueText?: string;
};

export const EmployeeAutocomplete = ({
  employees,
  name,
  label,
  disabled,
  onChange,
  value,
  errorMessage,
  helperText,
  emptyValueText,
}: Props) => {
  const { t } = useResource();

  const matchedEmployees = employees.filter(({ publicID }) =>
    value?.includes(publicID),
  );

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {label && (
          <Typography component="label" htmlFor={name}>
            {label}
          </Typography>
        )}
        <ButtonBase
          onClick={() => {
            const allEmployeeIds = employees.map(({ publicID }) => publicID);
            onChange(allEmployeeIds);
          }}
          disabled={disabled}
          sx={{
            fontSize: "0.875rem",
            textTransform: "none",
            color: disabled ? "text.secondary" : "primary.main",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <Typography fontWeight={500}>
            {t(COMPONENT_RESOURCES.SelectAll)}
          </Typography>
        </ButtonBase>
      </Box>
      <Autocomplete
        multiple
        options={employees}
        getOptionLabel={({ firstName, lastName }) => `${firstName} ${lastName}`}
        value={matchedEmployees}
        onChange={(_, newValues) => {
          onChange(newValues.map(({ publicID }) => publicID));
        }}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              matchedEmployees.length
                ? ""
                : (emptyValueText ?? t(COMPONENT_RESOURCES.Placeholder))
            }
            fullWidth
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: errorMessage ? "error.main" : "divider",
                borderRadius: 1,
              },
            }}
            error={!!errorMessage}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map(({ publicID, firstName, lastName }, index) => (
            <Chip
              {...getTagProps({ index })}
              key={publicID}
              label={`${firstName} ${lastName}`}
              deleteIcon={<CloseIcon />}
              onDelete={() => {
                const newValues = value
                  .filter((_, i) => i !== index)
                  .map(({ publicID }) => publicID);
                onChange(newValues);
              }}
            />
          ))
        }
        renderOption={(props, { firstName, lastName, email }) => (
          <Box component="li" display="flex" gap={1} {...props}>
            <Typography fontWeight={400}>
              {firstName} {lastName}
            </Typography>
            {email && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={300}
              >
                {email}
              </Typography>
            )}
          </Box>
        )}
        isOptionEqualToValue={(option, value) =>
          option.publicID === value.publicID
        }
        fullWidth
      />
      <FormHelperText
        error={!!errorMessage}
        sx={{ mt: 0.5, minHeight: "20px" }}
      >
        {errorMessage || helperText || " "}
      </FormHelperText>
    </div>
  );
};
